import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TemplateMapperConfig, EmailTemplate, EntityFieldSource, FieldSourceKind } from '../base-template-mapper';
import { SampleBasedTemplateMapper, FILTER_KEYS } from '../sample-based-template-mapper';
import { TemplateContext, ReportTemplate } from '../../../models/reportTemplate.model';
import { Sample } from '../../../models/sample.model';
import { DateFormatService } from '../../dateFormat.service';
import { TranslateService } from '../../translate.service';
import { UserService } from '../../user.service';
import { UserProfile } from '../../../models/userProfile.model';
import { ENTITY_TYPES } from 'src/app/shared/widget-registry';
import { CustomField } from '../../../models/customField.model';
import { convertImageUrlToBase64 } from '../../../_helpers/image.helper';
import { MultiSampleTablePresentationService, MultiSampleDetailedResultRow, DetailedResultSegRating } from '../../../components/template-widgets/sample-widgets/multi-sample-table-widget/multi-sample-table-presentation.service';
import { LikelihoodColorLevel, ResolvedExposureBand } from '../../../models/risk-assessment.model';
import { ReportRiskLevelService } from '../report-risk-level.service';
import { SegProfileService } from '../../seg-profile.service';
import { MethodOfAssessment } from '../../../enums/methodOfAssessment.enum';

/** One jurisdiction's full band table for the report, so a template can render a legend from config. */
export interface ExposureBandTableEntry {
  jurisdictionId: string;
  jurisdictionName: string;
  bands: ResolvedExposureBand[];
}

@Injectable({ providedIn: 'root' })
export class MultiSampleTemplateMapper extends SampleBasedTemplateMapper<Sample[]> {
  readonly config: TemplateMapperConfig = {
    entityType: ENTITY_TYPES.MULTI_SAMPLE,
    supportedFilters: [FILTER_KEYS.sampleNumber, FILTER_KEYS.sampleStatus, FILTER_KEYS.sampleCategory, FILTER_KEYS.agent, FILTER_KEYS.jurisdiction, FILTER_KEYS.oelType, FILTER_KEYS.segId]
  };

  private userData?: UserProfile;
  private tenantLogoImg?: string;
  private userProfileLoaded = false;
  private tenantLogoLoaded = false;

  constructor(
    dateFormatService: DateFormatService,
    translateService: TranslateService,
    private userService: UserService,
    multiSampleTablePresentationService: MultiSampleTablePresentationService,
    private reportRiskLevelService: ReportRiskLevelService,
    private segProfileService: SegProfileService
  ) { super(dateFormatService, translateService, multiSampleTablePresentationService); }

  protected getSamples(samples: Sample[]): Sample[] {
    return samples;
  }

  getEntityFieldSources(): EntityFieldSource[] {
    return [
      {
        key: 'summary',
        label: 'Summary',
        accent: 'var(--primary)',
        kind: FieldSourceKind.Inline,
        fields: [
          { path: 'sampleCount', label: 'Number of Samples' },
          { path: 'dateRange.startDate', label: 'Start Date' },
          { path: 'dateRange.endDate', label: 'End Date' },
          { path: 'sampledBy', label: 'Sampled By' },
        ],
      },
      {
        key: 'organization',
        label: 'Organization',
        accent: 'var(--warning)',
        kind: FieldSourceKind.Inline,
        fields: [
          { path: 'organization.name', label: 'Organization Name' },
          { path: 'organization.companyName', label: 'Company Name' },
          { path: 'organization.tenantLogoImg', label: 'Company Logo', isImage: true },
        ],
      },
      {
        key: 'detailedResultRows',
        label: 'Detailed Results',
        accent: 'var(--info)',
        kind: FieldSourceKind.Inline,
        fields: [
          { path: 'detailedResultRows.date', label: 'Result Date' },
          { path: 'detailedResultRows.sampleNumber', label: 'Sample Number' },
          { path: 'detailedResultRows.agentName', label: 'Agent' },
          { path: 'detailedResultRows.jurisdictionName', label: 'Jurisdiction' },
          { path: 'detailedResultRows.durationMinutes', label: 'Duration (min)' },
          { path: 'detailedResultRows.result', label: 'Result' },
          { path: 'detailedResultRows.exposureLimit', label: 'Exposure Limit' },
          { path: 'detailedResultRows.percentOfOel', label: '% of Exposure Limit' },
          { path: 'detailedResultRows.percentOfOelValue', label: '% of Exposure Limit (numeric)' },
          { path: 'detailedResultRows.exposureBandName', label: 'Exposure Band' },
          { path: 'detailedResultRows.exposureBandColor', label: 'Exposure Band Colour' },
          { path: 'detailedResultRows.exposureBand.name', label: 'Exposure Band — Name' },
          { path: 'detailedResultRows.exposureBand.quantitativeName', label: 'Exposure Band — Quantitative Name' },
          { path: 'detailedResultRows.exposureBand.color', label: 'Exposure Band — Colour' },
          { path: 'detailedResultRows.exposureBand.min', label: 'Exposure Band — Min % of OEL' },
          { path: 'detailedResultRows.exposureBand.max', label: 'Exposure Band — Max % of OEL' },
          { path: 'detailedResultRows.exposureBand.minSamples', label: 'Exposure Band — Min Samples' },
          { path: 'detailedResultRows.exposureBand.reassessmentFrequencyMonths', label: 'Exposure Band — Reassessment Frequency (months)' },
          { path: 'detailedResultRows.exposureBand.description', label: 'Exposure Band — Description' },
          { path: 'detailedResultRows.exposureBand.key', label: 'Exposure Band — Level Key' },
          { path: 'detailedResultRows.locationComments', label: 'Location Comments' },
          { path: 'detailedResultRows.segName', label: 'SEG Name' },
          { path: 'detailedResultRows.segRating', label: 'SEG Risk Rating' },
          { path: 'detailedResultRows.segRatingColor', label: 'SEG Rating Colour' },
          { path: 'detailedResultRows.methodOfAssessment', label: 'Method of Assessment' },
        ],
      },
      {
        key: 'exposureBandTable',
        label: 'Exposure Band Table',
        accent: 'var(--info)',
        kind: FieldSourceKind.Inline,
        fields: [
          { path: 'exposureBandTable.jurisdictionName', label: 'Jurisdiction' },
          { path: 'exposureBandTable.bands.name', label: 'Band — Name' },
          { path: 'exposureBandTable.bands.quantitativeName', label: 'Band — Quantitative Name' },
          { path: 'exposureBandTable.bands.color', label: 'Band — Colour' },
          { path: 'exposureBandTable.bands.min', label: 'Band — Min % of OEL' },
          { path: 'exposureBandTable.bands.max', label: 'Band — Max % of OEL' },
          { path: 'exposureBandTable.bands.minSamples', label: 'Band — Min Samples' },
          { path: 'exposureBandTable.bands.reassessmentFrequencyMonths', label: 'Band — Reassessment Frequency (months)' },
          { path: 'exposureBandTable.bands.description', label: 'Band — Description' },
          { path: 'exposureBandTable.bands.key', label: 'Band — Level Key' },
        ],
      },
    ];
  }

  async mapToTemplateContext(
    samples: Sample[],
    template: ReportTemplate,
    userInputs: Record<string, string | boolean>,
    filteredData?: unknown[]
  ): Promise<TemplateContext> {
    if (!this.userProfileLoaded) {
      await this.loadUserProfile();
    }

    const processedSamples = (filteredData as Sample[] | undefined) ?? samples;
    const sampledBy = Array.from(new Set(processedSamples.map(s => s.sampledByUsername).filter(Boolean))).sort().join(', ');
    const startDates = processedSamples.map(s => s.startDate).filter(Boolean).map(d => new Date(d as string | number | Date).getTime());
    const endDates = processedSamples.map(s => s.endDate).filter(Boolean).map(d => new Date(d as string | number | Date).getTime());
    const earliest = startDates.length ? new Date(Math.min(...startDates)) : undefined;
    const latest = endDates.length ? new Date(Math.max(...endDates)) : undefined;

    if (!this.tenantLogoLoaded) {
      this.tenantLogoImg = await this.loadTenantLogo();
    }
    const tenantLogoImg = this.tenantLogoImg;

    const multiSampleTable = this.buildMultiSampleTableContext(
      { samples: processedSamples },
      { selectedOelFilters: [], excludeAdjustedOel: false }
    );

    const tenantRiskLevels = await this.reportRiskLevelService.loadTenantRiskLevels();
    const jurisdictionIds = processedSamples
      .flatMap(s => (s.ihLabResults ?? []).map(r => r.jurisdictionId))
      .filter((id): id is string => !!id);
    const jurisdictionRiskLevels = await this.reportRiskLevelService.loadJurisdictionRiskLevels(jurisdictionIds);
    const jurisdictionRiskLevelMap = new Map<string, LikelihoodColorLevel[]>(Object.entries(jurisdictionRiskLevels));

    const detailedResultRows = await this.buildDetailedResultRows(processedSamples, tenantRiskLevels, jurisdictionRiskLevelMap);
    const exposureBandTable = this.buildExposureBandTable(processedSamples, tenantRiskLevels, jurisdictionRiskLevels);

    return {
      samples: processedSamples.map(sample => this.withResolvedSampleEnumLabels(sample)),
      sampleCount: processedSamples.length,
      dateRange: {
        startDate: this.formatDate(earliest),
        endDate: this.formatDate(latest)
      },
      sampledBy,
      organization: {
        name: this.userData?.companyName || 'N/A',
        companyName: this.userData?.companyName || 'N/A',
        tenantLogoImg
      },
      computedData: {
        samples: processedSamples,
        multiSampleTable,
        detailedResultRows,
        exposureBandTable
      },
      userInputs: { ...this.getDefaultUserInputs(), ...userInputs },
      ...this.buildSyntheticValues(this.userData, this.dateFormatService)
    };
  }

  private async buildDetailedResultRows(
    samples: Sample[],
    tenantRiskLevels: LikelihoodColorLevel[],
    jurisdictionRiskLevelMap: Map<string, LikelihoodColorLevel[]>
  ): Promise<MultiSampleDetailedResultRow[]> {
    if (!this.multiSampleTablePresentationService) return [];

    const segRatings = await this.loadSegRatings(samples);

    return this.multiSampleTablePresentationService.buildDetailedResultRows(
      { samples },
      {
        resolveExposureBand: (percentOfOel, jurisdictionId) =>
          this.reportRiskLevelService.resolveExposureBand(percentOfOel, jurisdictionId, tenantRiskLevels, jurisdictionRiskLevelMap),
        resolveSegRating: (segId, agentId) =>
          segRatings.get(this.segRatingKey(segId, agentId)) ?? { rating: '', ratingColor: '', methodOfAssessment: '' }
      }
    );
  }

  private buildExposureBandTable(
    samples: Sample[],
    tenantRiskLevels: LikelihoodColorLevel[],
    jurisdictionRiskLevels: Record<string, LikelihoodColorLevel[]>
  ): ExposureBandTableEntry[] {
    const nameById = new Map<string, string>();
    for (const sample of samples) {
      for (const result of sample.ihLabResults ?? []) {
        if (result.jurisdictionId && result.jurisdictionName) {
          nameById.set(result.jurisdictionId, result.jurisdictionName);
        }
      }
    }

    return [...nameById.entries()]
      .map(([jurisdictionId, jurisdictionName]) => ({
        jurisdictionId,
        jurisdictionName,
        bands: this.reportRiskLevelService.toBands(jurisdictionRiskLevels[jurisdictionId] ?? tenantRiskLevels)
      }))
      .sort((a, b) => a.jurisdictionName.localeCompare(b.jurisdictionName));
  }

  private async loadSegRatings(samples: Sample[]): Promise<Map<string, DetailedResultSegRating>> {
    const map = new Map<string, DetailedResultSegRating>();
    const orgLevelIds = [...new Set(samples.map(s => s.organizationLevelId).filter((id): id is string => !!id))];
    if (!orgLevelIds.length) return map;

    const summaries = await Promise.all(
      orgLevelIds.map(id =>
        firstValueFrom(
          this.segProfileService.getSegProfileSummary({ organizationLevelId: id, windowStart: null, windowEnd: null, agentId: null })
        ).catch(() => null)
      )
    );

    for (const summary of summaries) {
      for (const row of summary?.rows ?? []) {
        map.set(this.segRatingKey(row.segId, row.agentId), {
          rating: row.assessedBandName || '',
          ratingColor: row.assessedBandColor || '',
          methodOfAssessment: this.methodOfAssessmentLabel(row.methodOfAssessment)
        });
      }
    }

    return map;
  }

  private segRatingKey(segId: string | undefined, agentId: string): string {
    return `${segId ?? ''}|${agentId}`;
  }

  private methodOfAssessmentLabel(method: MethodOfAssessment | null | undefined): string {
    switch (method) {
      case MethodOfAssessment.Qualitative: return this.translateService.getDynamicTranslation('Qualitative');
      case MethodOfAssessment.Quantitative: return this.translateService.getDynamicTranslation('Quantitative');
      case MethodOfAssessment.DoesNotApply: return this.translateService.getDynamicTranslation('Does Not Apply');
      default: return '';
    }
  }

  getDefaultUserInputs(): Record<string, string> {
    return { description: '', notes: '' };
  }

  getEmailTemplate(samples: Sample[], _customFields?: CustomField[]): EmailTemplate {
    const count = samples?.length || 0;
    return {
      subject: 'Multi-sample exposure report',
      message: `Please find attached the exposure monitoring results for ${count} sample${count === 1 ? '' : 's'}.`,
      ccEmails: []
    };
  }

  private async loadUserProfile(): Promise<void> {
    try {
      this.userData = await firstValueFrom(this.userService.getCurrentUserProfile());
      this.userProfileLoaded = true;
    } catch {
      this.userData = new UserProfile();
    }
  }

  private async loadTenantLogo(): Promise<string> {
    try {
      const logoUrl = await firstValueFrom(this.userService.getTenantLogoUrl());
      const result = logoUrl ? await convertImageUrlToBase64(logoUrl) : '';
      this.tenantLogoLoaded = true;
      return result;
    } catch {
      return '';
    }
  }
}
